import type { AvailabilityDay, AvailabilityRule } from '~/features/provider-hlx/calendar/types';
import { addDays, toISODate } from '~/features/shared/calendar/dateUtils';
import type { ListingKind } from './types';

export interface AvailabilitySummaryRange {
  end: string;
  start: string;
}

/** Split the rolling three-year horizon into API-safe 366-day reads. */
export const buildAvailabilitySummaryRanges = (today: Date): AvailabilitySummaryRange[] => {
  const horizon = new Date(today.getFullYear() + 3, today.getMonth(), today.getDate());
  const ranges: AvailabilitySummaryRange[] = [];
  let start = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  while (start <= horizon) {
    const candidateEnd = addDays(start, 365);
    const end = candidateEnd < horizon ? candidateEnd : horizon;
    ranges.push({ start: toISODate(start), end: toISODate(end) });
    start = addDays(end, 1);
  }
  return ranges;
};

interface AvailabilityReadinessInput {
  calendarDays: AvailabilityDay[];
  kind: ListingKind;
  preferredStartDate: string;
  recurringRules: AvailabilityRule[];
  selectedListingId: number | null;
  todayISO: string;
}

/** Combine general supply with this listing's additions and ignore expired rules. */
export const isAvailabilityReady = ({
  calendarDays,
  kind,
  preferredStartDate,
  recurringRules,
  selectedListingId,
  todayISO,
}: AvailabilityReadinessInput): boolean => {
  if (kind === 'FULL_TIME') return Boolean(preferredStartDate);

  // General availability applies to every listing. Listing-scoped rows are
  // additive for that listing; the My Services toggle only chooses the Calendar
  // view to open and is not a persisted exclusion policy.
  const appliesToListing = (serviceListingId: number | null) =>
    serviceListingId === null ||
    (selectedListingId != null && serviceListingId === selectedListingId);

  if (kind === 'PART_TIME') {
    return recurringRules.some(
      (rule) =>
        appliesToListing(rule.serviceListingId) &&
        (rule.effectiveEndDate === null || rule.effectiveEndDate >= todayISO)
    );
  }

  return calendarDays.some((day) =>
    day.entries.some(
      (entry) => entry.status === 'AVAILABLE' && appliesToListing(entry.serviceListingId)
    )
  );
};
